import React, { useContext, useMemo, useState } from 'react';
import type { GridApi } from 'ag-grid-community';
import Row from 'components/Common/Row';
import ExportButton from 'components/Grid/ExportButton';
import PeriodSelector from 'components/Board/PeriodSelector';
import OmniSearch from 'components/OmniSearch';
import FilterPlates from 'components/FilterPlates';
import { SALES_COMMISSION_ACTIONS_ELEMENT_ID } from 'components/Board/constants';
import QuickFilters from 'components/CustomerLaneCommitsBoard/QuickFilters';
import ClearQuickFiltersButton from 'components/CustomerLaneCommitsBoard/QuickFilters/ClearQuickFiltersButton';
import Grid from '../PerformanceBoard/Grid';
import useShouldAggregateSignOff from '../hooks/useShouldAggregateSignOff';
import PermissionGates from 'components/PermissionGates';
import PERMISSIONS from 'permissionDefinitions';
import BoardContext from '../../../contexts/BoardContext';
import WorkSpaceContext from '../../../contexts/WorkSpaceContext';
import BonusPeriodsContext from '../../../contexts/BonusPeriodsContext';
import flattenBonusSubPeriods from '../../../contextProviders/BonusAppProvider/flattenBonusSubPeriods';
import Inputs from '../../../components/Inputs';
import {
  BoardFilterBar,
  BoardMainContent,
  BoardQuickFilterBounds,
  BoardTopBar,
  BoardWrapper,
} from 'components/CustomerLaneCommitsBoard/styles';
import Typography from 'kingpin/atoms/Typography';
import { BoardFreshnessTooltip } from '../../DatasetFreshness';
import BonusBoardSignOff from './BonusBoardSignOff';
import AggregatedBoardCsvExportButton from './AggregatedBoardCsvExportButton';
import BoardContextMenu from './BoardContextMenu';
import V3PublishOrShareButton from '../../../screens/V3/Content/V3PublishOrShareButton';
import V3ContentBackButton from '../../../screens/V3/Content/V3ContentBackButton';

const BonusPeriodPicker = () => {
  const {
    bonusPeriods,
    selectedBonusPeriod,
    setSelectedBonusPeriod,
    selectedSubPeriod,
    setSelectedSubPeriod,
  } = useContext(BonusPeriodsContext);

  // A board is a content surface, so when the programme uses sub-periods this
  // lists the weeks; otherwise it lists the periods (the ordinary case).
  const subPeriodOptions = useMemo(
    () => flattenBonusSubPeriods(bonusPeriods),
    [bonusPeriods],
  );

  const options = useMemo((): DropdownOption[] => {
    if (subPeriodOptions) {
      return subPeriodOptions.map(({ period, subPeriod }) => ({
        key: `${period.id}:${subPeriod.startDate}`,
        label: subPeriod.label,
        isSelected:
          !!selectedSubPeriod &&
          selectedSubPeriod.startDate === subPeriod.startDate &&
          selectedBonusPeriod?.id === period.id,
        onSelected: () => {
          setSelectedBonusPeriod(period);
          setSelectedSubPeriod?.(subPeriod);
        },
      }));
    }

    return bonusPeriods.map((p) => ({
      key: p.id,
      label: p.label,
      isSelected: selectedBonusPeriod ? selectedBonusPeriod.id === p.id : false,
      onSelected: () => setSelectedBonusPeriod(p),
    }));
  }, [
    subPeriodOptions,
    bonusPeriods,
    selectedBonusPeriod,
    selectedSubPeriod,
    setSelectedBonusPeriod,
    setSelectedSubPeriod,
  ]);

  return <Inputs.Dropdown options={options} />;
};

const PerformanceBoard = ({
  board,
  gridApi,
  setGridApi,
  sortOverride,
  dataTypes,
  onManualFilterChanged,
  onQuickFilterChanged,
  isBonusPeriodDriven,
  isPeriodSelectorForced,
}: {
  board: PerformanceBoardTypes.Board;
  gridApi: GridApi | undefined;
  setGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
  sortOverride: SimpleGridSort;
  dataTypes: string[];
  onQuickFilterChanged: () => void;
  onManualFilterChanged: () => void;
  isBonusPeriodDriven?: boolean;
  isPeriodSelectorForced?: boolean;
}) => {
  const { quickFilters } = useContext(BoardContext);
  const { workSpace } = useContext(WorkSpaceContext);
  const isWorkSpace = !!workSpace;
  const hasQuickFilters = quickFilters.length > 0;
  // Decided once here so the grid's datasource and the export can't disagree:
  // an aggregated grid already holds every row, so it exports via ag-grid CSV.
  const isAggregated = useShouldAggregateSignOff(board);
  // Set when DataTable draws the grid: the exports read it in place of the
  // grid's api
  const [exportHandle, setExportHandle] = useState<DataTable.ExportHandle>();

  return (
    <BoardWrapper>
      <BoardTopBar>
        <Row centerAlign style={{ gap: 8 }}>
          <V3ContentBackButton />
          <Typography.Header type="H5">{board.name}</Typography.Header>
          <BoardFreshnessTooltip board={board} />
        </Row>
        <Row centerAlign style={{ gap: 8 }}>
          <BonusBoardSignOff board={board} />
          <Row centerAlign style={{ gap: 8 }}>
            {isBonusPeriodDriven && <BonusPeriodPicker />}
            {gridApi && (
              <div>
                {isAggregated ? (
                  <AggregatedBoardCsvExportButton
                    gridApi={exportHandle ? undefined : gridApi}
                    exportHandle={exportHandle}
                    exportName={board.name}
                  />
                ) : (
                  <ExportButton
                    overrideGridApi={exportHandle ? undefined : gridApi}
                    exportHandle={exportHandle}
                    exportName={board.name}
                  />
                )}
              </div>
            )}
            <PeriodSelector
              isWorkSpaceTopBar={false}
              isForced={isPeriodSelectorForced}
            />
          </Row>
          <PermissionGates.Has
            requiredPermission={PERMISSIONS.PERFORMANCE_BOARD.VIEW_SETTINGS}
          >
            <V3PublishOrShareButton />
            <BoardContextMenu board={board} />
          </PermissionGates.Has>
        </Row>
      </BoardTopBar>
      <BoardFilterBar hasBorder={hasQuickFilters}>
        <Row centerAlign>
          <div style={{ marginRight: 8, width: 150 }}>
            <OmniSearch.DrillDownBuilder
              dataTypes={dataTypes}
              onManualFilterChanged={onManualFilterChanged}
            />
          </div>
          <FilterPlates onManualFilterChanged={onManualFilterChanged} />
        </Row>
        <div id={SALES_COMMISSION_ACTIONS_ELEMENT_ID} />
      </BoardFilterBar>
      {hasQuickFilters && (
        <BoardFilterBar>
          <BoardQuickFilterBounds>
            {/* Aggregated boards show a row per driver, not per record */}
            <QuickFilters
              onQuickFilterChanged={onQuickFilterChanged}
              showCounts={!isAggregated}
            />
          </BoardQuickFilterBounds>
          <ClearQuickFiltersButton
            onQuickFilterChanged={onQuickFilterChanged}
          />
        </BoardFilterBar>
      )}
      <BoardMainContent
        isWorkSpace={isWorkSpace}
        hasQuickFilters={hasQuickFilters}
      >
        <Grid
          board={board}
          isAggregated={isAggregated}
          sortOverride={sortOverride}
          key={board.updatedOn}
          gridApi={gridApi}
          setGridApi={setGridApi}
          setExportHandle={setExportHandle}
        />
      </BoardMainContent>
    </BoardWrapper>
  );
};

export default PerformanceBoard;
